import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleStop,
  Clock,
  Download,
  Loader2,
  RefreshCw,
  TriangleAlert,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  api,
  isDescriptionsResult,
  isImageResult,
  isStockResult,
  type DescriptionsJobResult,
  type ImageSyncJobResult,
  type IngestionJobResult,
  type IngestionJobReturnValue,
  type IngestionJobSummary,
  type RowSkipReason,
} from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { downloadBlob } from "@/lib/download-blob";
import { formatDuration, formatTimestamp } from "@/lib/job-format";
import { refusalNote } from "@/lib/zeroing-refusal";

const POLL_INTERVAL_MS = 5_000;

// Thousands grouped: 21,701.
const NUMBER = new Intl.NumberFormat("en-GB");

// The `<epoch>-<uuid>-` prefix storage adds to uploaded file names.
const KEY_PREFIX = /^\d+-[0-9a-f-]{36}-/i;

// Page size is the number of rows that fit in the window, so the list never
// scrolls. This height is used until a row is measured.
const ESTIMATED_ROW_HEIGHT = 49;
// Space below the card: the dashboard's bottom padding.
const PAGE_BOTTOM_GAP = 24;

// So the detail row spans every column.
const COLUMN_COUNT = 7;

// Every state but completed and failed.
const STOPPABLE_STATES = new Set([
  "active",
  "waiting",
  "waiting-children",
  "prioritized",
  "delayed",
  "paused",
]);

interface StateStyle {
  label: string;
  icon: LucideIcon;
  variant: "default" | "secondary" | "destructive" | "outline" | "success";
  spin?: boolean;
}

// A completed pass: green needs nobody, amber (the panel accent) is worth
// opening.
const SUCCESS: StateStyle = { label: "Success", icon: CheckCircle2, variant: "success" };
const WARNING: StateStyle = { label: "Warning", icon: TriangleAlert, variant: "default" };

// Icon and text on every badge, never colour alone.
const STATE_STYLES: Record<string, StateStyle> = {
  failed: { label: "Failed", icon: XCircle, variant: "destructive" },
  active: { label: "Running", icon: Loader2, variant: "secondary", spin: true },
  waiting: { label: "Waiting", icon: Clock, variant: "outline" },
  "waiting-children": { label: "Waiting", icon: Clock, variant: "outline" },
  prioritized: { label: "Waiting", icon: Clock, variant: "outline" },
  delayed: { label: "Retrying", icon: Clock, variant: "outline" },
};

// BullMQ marks any pass that returned as completed; its counters decide
// Success or Warning.
function stateStyle(job: IngestionJobSummary): StateStyle {
  if (job.state === "completed") return needsAttention(job) ? WARNING : SUCCESS;
  return (
    STATE_STYLES[job.state] ?? { label: job.state, icon: Clock, variant: "outline" }
  );
}

// Finished but left work for a person: rows it could not read, brands it could
// not place, refused zeroing, or a stop. Not counted: routine skips (every feed
// has thousands), the per-run cap (the next run continues), stock quantities.
function needsAttention(job: IngestionJobSummary): boolean {
  const result = job.returnValue;
  if (!result) return false;
  if (result.cancelled) return true;
  if (isImageResult(result)) return result.imageErrors > 0;
  if (isDescriptionsResult(result)) return result.descriptionErrors > 0;
  return (
    result.rowErrors > 0 ||
    result.brandsNotFound > 0 ||
    unresolvedBrands(job) !== null ||
    result.zeroingRefused !== null
  );
}

export function IngestionJobsCard() {
  const [pageSize, setPageSize] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Only grows, so after a page of taller rows (Stop button, retry count) the
  // next measure never hands out more rows than fit.
  const rowHeight = useRef(ESTIMATED_ROW_HEIGHT);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["ingestion-jobs", pageSize, offset],
    queryFn: () => api.ingestionJobs(pageSize ?? undefined, offset),
    enabled: pageSize !== null,
    placeholderData: keepPreviousData,
    // Polled: jobs enqueued outside the panel (the nightly run, a curl call)
    // raise no local event.
    refetchInterval: POLL_INTERVAL_MS,
  });

  const jobs = data?.jobs ?? [];
  const total = data?.total ?? 0;

  // Rows that fit from the list's top to the window's bottom, minus what the
  // card shows under the list (the pager) and PAGE_BOTTOM_GAP.
  const measure = useCallback(() => {
    const card = cardRef.current;
    const body = bodyRef.current;
    if (!card || !body) return;
    for (const row of body.querySelectorAll<HTMLElement>("tr[data-job-row]")) {
      rowHeight.current = Math.max(rowHeight.current, row.offsetHeight);
    }
    const rows = body.querySelectorAll<HTMLElement>("tr[data-job-row], tr[data-job-detail]");
    const listed = [...rows].reduce((sum, row) => sum + row.offsetHeight, 0);
    const bodyRect = body.getBoundingClientRect();
    const listTop = bodyRect.top + window.scrollY + (bodyRect.height - listed);
    const below = card.getBoundingClientRect().bottom - bodyRect.bottom + PAGE_BOTTOM_GAP;
    const fits = Math.floor((window.innerHeight - listTop - below) / rowHeight.current);
    setPageSize(Math.max(1, fits));
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, jobs.length, isLoading, isError]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  // Jobs expire, so the current page can empty; fall back to the last page.
  useEffect(() => {
    if (data && offset > 0 && offset >= total) {
      setOffset(pageSize === null ? 0 : Math.max(0, Math.floor((total - 1) / pageSize) * pageSize));
    }
  }, [data, offset, total, pageSize]);

  const size = pageSize ?? 1;
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + jobs.length, total);

  return (
    <Card ref={cardRef}>
      <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>Ingestion jobs</CardTitle>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={isFetching ? "size-4 animate-spin" : "size-4"} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <div ref={bodyRef}>
          {isLoading || pageSize === null ? (
            <div className="space-y-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : isError ? (
            <p className="text-sm text-destructive">
              Could not load the jobs
              {error instanceof Error ? `: ${error.message}` : "."}
            </p>
          ) : jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No ingestion jobs yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full caption-bottom text-sm">
                <caption className="sr-only">
                  Ingestion jobs, newest first
                </caption>
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      Supplier
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      File
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      Status
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      Queued
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      Duration
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      Logs
                    </th>
                    <th scope="col" className="pb-2 font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <JobRow key={job.id} job={job} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {total > 0 && (
          <nav
            aria-label="Ingestion jobs pages"
            className="flex items-center justify-between gap-4 pt-3 text-sm text-muted-foreground"
          >
            <span aria-live="polite">
              {first}–{last} of {NUMBER.format(total)}
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset((current) => Math.max(0, current - size))}
              >
                <ChevronLeft className="size-4" />
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={offset + size >= total}
                onClick={() => setOffset((current) => current + size)}
              >
                Next
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </nav>
        )}
      </CardContent>
    </Card>
  );
}

function JobRow({ job }: { job: IngestionJobSummary }) {
  const style = stateStyle(job);
  const Icon = style.icon;
  const detailId = useId();
  const brandsLabelId = `${detailId}-brands`;
  const [expanded, setExpanded] = useState(false);
  // The full failure message, shown when expanded.
  const detail = job.failedReason;
  // Listed by name: the names are what someone acts on.
  const brands = unresolvedBrands(job);
  const breakdown = job.returnValue ? describeResult(job.returnValue) : null;
  const expandable = Boolean(detail) || brands !== null || breakdown !== null;

  // Ignores the click that ends a text selection (copying a file name).
  const toggle = () => {
    if (window.getSelection()?.isCollapsed === false) return;
    setExpanded((open) => !open);
  };

  const queryClient = useQueryClient();
  const stop = useMutation({
    mutationFn: () => api.cancelIngestionJob(job.id),
    onSuccess: (result) => {
      if (result.action === "requested") {
        toast.success(
          `${job.supplier ?? job.id}: stopping. It will save what it did and exit in a few seconds`,
        );
      } else if (result.action === "removed") {
        toast.success(`${job.supplier ?? job.id}: removed from the queue`);
      } else {
        toast.info(`${job.supplier ?? job.id}: had already finished`);
      }
      void queryClient.invalidateQueries({ queryKey: ["ingestion-jobs"] });
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? `Could not stop: ${error.message}` : "Could not stop",
      );
    },
  });

  return (
    <>
      <tr
        data-job-row
        className={
          expandable
            ? "cursor-pointer border-b border-border align-top last:border-0 hover:bg-muted/50"
            : "border-b border-border align-top last:border-0"
        }
        onClick={expandable ? () => toggle() : undefined}
      >
        <td className="py-2 pr-4 font-medium">
          {expandable ? (
            // The row toggles on click; this button is the keyboard and
            // screen-reader control, styled as the supplier name.
            <button
              type="button"
              className="rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-expanded={expanded}
              aria-controls={detailId}
              onClick={(event) => {
                event.stopPropagation();
                toggle();
              }}
            >
              {supplierLabel(job)}
              {/* Names the detail: a failed job's error, a finished job's
                  counters. */}
              <span className="sr-only">
                {" "}
                ({expanded ? "hide" : "show"}{" "}
                {detail ? "the full error of" : "the details of"} {jobLabel(job)})
              </span>
            </button>
          ) : (
            supplierLabel(job)
          )}
        </td>
        <td className="py-2 pr-4">
          <span className="block max-w-[22ch] truncate" title={job.fileName ?? undefined}>
            {sourceLabel(job)}
          </span>
        </td>
        <td className="py-2 pr-4">
          <Badge variant={style.variant} className="gap-1.5 whitespace-nowrap">
            <Icon className={style.spin ? "size-3.5 animate-spin" : "size-3.5"} />
            {style.label}
          </Badge>
          {job.attemptsMade > 1 && (
            <span className="mt-1 block text-xs text-muted-foreground">
              {job.attemptsMade} attempts
            </span>
          )}
        </td>
        <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
          {formatTimestamp(job.createdAt)}
        </td>
        <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
          {formatDuration(job.processedAt, job.finishedAt)}
        </td>
        <td className="py-2 pr-4">
          <ErrorReportButton job={job} />
        </td>
        <td className="py-2 text-right">
          {STOPPABLE_STATES.has(job.state) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5 whitespace-nowrap"
              disabled={stop.isPending}
              onClick={(event) => {
                event.stopPropagation();
                stop.mutate();
              }}
            >
              {stop.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <CircleStop className="size-3.5" />
              )}
              Stop
            </Button>
          )}
        </td>
      </tr>
      {expandable && expanded && (
        <tr data-job-detail className="border-b border-border last:border-0">
          <td colSpan={COLUMN_COUNT} className="pb-3">
            <div id={detailId} className="space-y-3">
              {breakdown && (
                <div className="rounded-md bg-muted p-3 text-xs leading-relaxed">
                  {/* Two label/value pairs per line where there is width, one
                      otherwise; values aligned in a column. */}
                  <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 sm:grid-cols-[1fr_auto_1fr_auto]">
                    {breakdown.counters.map(([label, value]) => (
                      <Fragment key={label}>
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="text-right font-mono tabular-nums text-foreground">
                          {typeof value === "number" ? NUMBER.format(value) : value}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                  {/* Why the counters mean less than they look: a stop, a cap,
                      a refused zeroing. */}
                  {breakdown.notes.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {breakdown.notes.map((note) => (
                        <p
                          key={note.text}
                          className={note.alarming ? "text-destructive" : "text-muted-foreground"}
                        >
                          {note.text}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {detail && (
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-xs leading-relaxed text-foreground">
                  {detail}
                </pre>
              )}
              {brands && (
                <div className="rounded-md bg-muted p-3 text-xs leading-relaxed">
                  <p id={brandsLabelId} className="font-medium text-foreground">
                    Unrecognised brands ({brands.names.length}
                    {brands.truncated && "+"})
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    None matches a catalogue brand, so their rows created no product. Add
                    an alias in brand-map.data.ts, create the brand by hand or blacklist it,
                    then run the ingestion again.
                  </p>
                  {/* Monospaced: copied into brand-map.data.ts exactly as written. */}
                  <ul aria-labelledby={brandsLabelId} className="mt-2 flex flex-wrap gap-1.5">
                    {brands.names.map((name) => (
                      <li
                        key={name}
                        className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-foreground"
                      >
                        {name}
                      </li>
                    ))}
                  </ul>
                  {brands.truncated && (
                    <p className="mt-2 text-muted-foreground">
                      The list is truncated; the error CSV names every row.
                    </p>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Fetched as a blob (see requestFile) and saved from memory.
function ErrorReportButton({ job }: { job: IngestionJobSummary }) {
  const key = errorReportKey(job);

  const download = useMutation({
    mutationFn: () => api.ingestionErrorReport(job.id),
    onSuccess: (blob) => downloadBlob(blob, reportFileName(job)),
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? `Could not download the log: ${error.message}`
          : "Could not download the log",
      );
    },
  });

  // No errors, no report.
  if (key === null) return <span className="text-muted-foreground">—</span>;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="gap-1.5 whitespace-nowrap"
      disabled={download.isPending}
      onClick={(event) => {
        event.stopPropagation();
        download.mutate();
      }}
    >
      {download.isPending ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Download className="size-3.5" />
      )}
      {/* Kept out of the accessible name, which the sr-only text gives. */}
      <span aria-hidden="true">CSV</span>
      <span className="sr-only">Download the error log of {jobLabel(job)}</span>
    </Button>
  );
}

// Supplier and date rather than the storage key, which carries a uuid.
function reportFileName(job: IngestionJobSummary): string {
  const stamp = job.createdAt ? new Date(job.createdAt).toISOString().slice(0, 10) : "unknown";
  return `${job.supplier ?? job.kind}-${stamp}-errors.csv`;
}

// Where a failed pass puts its report key; same format as the API's
// FAILED_REASON_REPORT_RE.
const FAILED_REASON_REPORT = /\(error report: (\S+)\)/;

function errorReportKey(job: IngestionJobSummary): string | null {
  return (
    job.returnValue?.errorReportKey ??
    FAILED_REASON_REPORT.exec(job.failedReason ?? "")?.[1] ??
    null
  );
}

// Labels for RowSkipReason.
const SKIP_LABELS: Record<RowSkipReason, string> = {
  "no-reference": "no reference",
  "no-condition": "no condition",
  "condition-not-sold": "condition we do not sell",
  "lead-time-not-sold": "lead time we do not sell",
  "no-price": "no price",
  "price-below-minimum": "price below €1",
};

function skippedRows(result: IngestionJobResult): Array<readonly [string, number]> {
  return (Object.entries(result.rowsSkipped ?? {}) as Array<[RowSkipReason, number]>)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([reason, n]) => [SKIP_LABELS[reason] ?? reason, n] as const);
}

// Null for non-stock passes and passes that placed every brand.
function unresolvedBrands(job: IngestionJobSummary) {
  const result = job.returnValue;
  if (!result || !isStockResult(result)) return null;
  const unresolved = result.unresolvedBrands;
  return unresolved && unresolved.names.length > 0 ? unresolved : null;
}


// Every counter of a finished pass, labelled, plus notes qualifying them. Same
// fields in the same order every time, zeros included, so the block also shows
// what the pass did not do.
interface Breakdown {
  counters: ReadonlyArray<readonly [string, number | string]>;
  notes: ReadonlyArray<{ text: string; alarming?: boolean }>;
}

function describeResult(result: IngestionJobReturnValue): Breakdown {
  if (isImageResult(result)) return describeImageResult(result);
  if (isDescriptionsResult(result)) return describeDescriptionsResult(result);
  return describeStockResult(result);
}

function describeStockResult(result: IngestionJobResult): Breakdown {
  const skipped = skippedRows(result);
  const counters: ReadonlyArray<readonly [string, number | string]> = [
    ["Rows read", result.rowsProcessed],
    ["Rows skipped by rule", skipped.reduce((sum, [, n]) => sum + n, 0)],
    ...skipped.map(([label, n]) => [`· ${label}`, n] as const),
    ["Rows rejected", result.rowErrors],
    ["Rows without brand", result.brandsNotFound],
    ["Products created", result.productsInserted],
    ["Prices created", result.pricesInserted],
    ["Prices updated", result.pricesUpdated],
    ["Stock updated", result.stockUpdated],
    ["New images", result.imagesInserted],
    ["Images linked", result.priceImagesLinked],
    ["Prices zeroed", result.pricesZeroed],
  ];

  const notes: Array<{ text: string; alarming?: boolean }> = [];
  if (result.cancelled) {
    notes.push({
      text: "Stopped by hand: what was read is saved and nothing was zeroed, because the feed was left half done.",
    });
  }
  if (result.zeroingRefused) {
    notes.push({ text: refusalNote(result.zeroingRefused), alarming: true });
  }
  if (result.errorReportLost) {
    notes.push({
      text:
        `The error report could not be saved (${result.errorReportLost}): ` +
        `${result.rowErrors} rejected rows are counted above but cannot be listed.`,
      alarming: true,
    });
  }
  return { counters, notes };
}

function describeImageResult(result: ImageSyncJobResult): Breakdown {
  const notes: Array<{ text: string; alarming?: boolean }> = [];
  if (result.stoppedAtLimit) {
    notes.push({
      text: "The per-run limit was reached: the next run picks up where this one left off.",
    });
  }
  if (result.cancelled) {
    notes.push({ text: "Stopped by hand: what was downloaded is saved." });
  }
  return {
    counters: [
      ["Prices without image", result.pricesNeedingImages],
      ["Items looked up", result.itemsTargeted],
      ["Items found in feed", result.itemsFoundInFeed],
      ["Images downloaded", result.imagesDownloaded],
      ["Images reused", result.imagesReused],
      ["Linked to prices", result.priceImagesLinked],
      ["Linked to products", result.productImagesLinked],
      ["Skipped as generic", result.skippedGeneric],
      ["Skipped over cap", result.skippedOverCap],
      ["Errors", result.imageErrors],
    ],
    notes,
  };
}

function describeDescriptionsResult(result: DescriptionsJobResult): Breakdown {
  const notes: Array<{ text: string; alarming?: boolean }> = [];
  if (result.stoppedAtLimit) {
    notes.push({
      text: "The per-run limit was reached: the next run describes the rest.",
    });
  }
  if (result.cancelled) {
    notes.push({ text: "Stopped by hand: what was written stays written." });
  }
  return {
    counters: [
      ["Products pending", result.productsNeedingDescription],
      ["Descriptions written", result.generated],
      ["Already filled elsewhere", result.alreadyFilled],
      ["Errors", result.descriptionErrors],
    ],
    notes,
  };
}

// The descriptions pass has no supplier; its `supplier` field is only a label.
function supplierLabel(job: IngestionJobSummary): string {
  if (job.kind === "descriptions") return "AI descriptions";
  return job.supplier ?? "—";
}

function sourceLabel(job: IngestionJobSummary): string {
  if (job.kind === "images") return "API (images)";
  if (job.kind === "descriptions") return "All suppliers";
  return displayFileName(job.fileName);
}

// The job in screen-reader labels: the file for an upload, otherwise the pass.
function jobLabel(job: IngestionJobSummary): string {
  if (job.kind === "descriptions") return "the descriptions run";
  if (job.kind === "images") return `the ${job.supplier ?? "?"} images`;
  return displayFileName(job.fileName);
}

// No file means the API fetched the feed.
function displayFileName(fileName: string | null): string {
  if (!fileName) return "API";
  return fileName.replace(KEY_PREFIX, "");
}


